import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useMutation, useQueries, useQuery, useQueryClient } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { Save } from "lucide-react";

import { type Client, type ClientMeter, clientsApi } from "@/api/clients";
import { telemetryApi } from "@/api/telemetry";
import { useCanEdit } from "@/hooks/useRole";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { cn } from "@/lib/utils";

type Tab = "register" | "manual";

interface HeatMeterRow {
  meter: ClientMeter;
  client: Client;
}

import { todayIso } from "@/lib/format";

// Локальна shorthand: "HH" без ":00" (canonical fmtHourLocal повертає "HH:00").
const padHour = (hour: number): string => String(hour).padStart(2, "0");

function isoForHour(date: string, hour: number): string {
  // YYYY-MM-DDTHH:00:00 в Kyiv-локалі — бекенд floor'ить до години.
  const d = new Date(`${date}T${padHour(hour)}:00:00`);
  return d.toISOString();
}

export function HeatMetersPage() {
  const { t } = useTranslation();
  const [tab, setTab] = useState<Tab>("register");

  const clients = useQuery({
    queryKey: ["clients"],
    queryFn: () => clientsApi.list(false),
  });

  // For each client fetch heat meters; flatten the result.
  const heatQueries = useQueries({
    queries: (clients.data ?? []).map((c) => ({
      queryKey: ["client-meters", c.id, "heat"] as const,
      queryFn: () => clientsApi.listMeters(c.id, "heat"),
      enabled: !!clients.data,
    })),
  });

  const rows = useMemo<HeatMeterRow[]>(() => {
    if (!clients.data) return [];
    const out: HeatMeterRow[] = [];
    clients.data.forEach((c, i) => {
      const meters = heatQueries[i]?.data ?? [];
      meters.forEach((m) => out.push({ client: c, meter: m }));
    });
    return out;
  }, [clients.data, heatQueries]);

  const loading = clients.isLoading || heatQueries.some((q) => q.isLoading);

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle>{t("heat_meters.title")}</CardTitle>
          <CardDescription>{t("heat_meters.subtitle")}</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex gap-1 rounded-lg border border-slate-200 bg-slate-50 p-1 w-fit">
            {(["register", "manual"] as const).map((m) => (
              <button
                key={m}
                type="button"
                onClick={() => setTab(m)}
                className={cn(
                  "rounded-md px-3 py-1.5 text-sm transition-colors",
                  tab === m ? "bg-white shadow-sm font-medium" : "text-slate-600 hover:text-slate-900",
                )}
              >
                {t(`heat_meters.tab_${m}`)}
              </button>
            ))}
          </div>
        </CardContent>
      </Card>

      {tab === "register" ? (
        <RegisterTab rows={rows} loading={loading} />
      ) : (
        <ManualTab rows={rows} loading={loading} />
      )}
    </div>
  );
}

function RegisterTab({ rows, loading }: { rows: HeatMeterRow[]; loading: boolean }) {
  const { t } = useTranslation();
  return (
    <Card>
      <CardContent className="p-0">
        {loading ? (
          <div className="p-6 text-sm text-slate-500">{t("common.loading")}</div>
        ) : rows.length === 0 ? (
          <div className="p-6 text-sm text-slate-500">{t("heat_meters.no_meters")}</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50">
                <tr className="text-left text-slate-500">
                  <th className="p-2">{t("heat_meters.client")}</th>
                  <th className="p-2">{t("heat_meters.label")}</th>
                  <th className="p-2">{t("heat_meters.meter_id")}</th>
                  <th className="p-2">source_id</th>
                </tr>
              </thead>
              <tbody>
                {rows.map(({ meter, client }) => (
                  <tr key={meter.id} className="border-t border-slate-100">
                    <td className="p-2">{client.name}</td>
                    <td className="p-2">{meter.name}</td>
                    <td className="p-2 font-mono text-xs">{meter.id}</td>
                    <td className="p-2 font-mono text-xs text-slate-500">
                      {meter.source_id ?? "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function ManualTab({ rows, loading }: { rows: HeatMeterRow[]; loading: boolean }) {
  const { t } = useTranslation();
  const canEdit = useCanEdit();
  const qc = useQueryClient();
  const [date, setDate] = useState(todayIso());
  const [meterId, setMeterId] = useState<string>("");

  const selected = rows.find((r) => r.meter.id === meterId) ?? null;

  // Initialize meterId once rows arrive
  if (!meterId && rows.length > 0) {
    setMeterId(rows[0].meter.id);
  }

  const points = useQuery({
    queryKey: ["telemetry-by-meter", meterId, date],
    queryFn: () => telemetryApi.byMeter(meterId, date),
    enabled: !!meterId,
  });

  // Map: hour -> existing value
  const byHour = useMemo(() => {
    const m = new Map<number, number>();
    (points.data ?? []).forEach((p) => {
      const h = new Date(p.ts).getHours(); // local browser hour; assumes Kyiv == local
      const v = typeof p.value === "string" ? Number(p.value) : p.value;
      if (v !== null && Number.isFinite(v)) m.set(h, v);
    });
    return m;
  }, [points.data]);

  return (
    <Card>
      <CardContent className="space-y-3">
        {loading ? (
          <div className="text-sm text-slate-500">{t("common.loading")}</div>
        ) : rows.length === 0 ? (
          <div className="text-sm text-slate-500">{t("heat_meters.no_meters")}</div>
        ) : (
          <>
            <div className="flex flex-wrap gap-3">
              <div className="space-y-1">
                <Label>{t("heat_meters.manual_date")}</Label>
                <Input
                  type="date"
                  value={date}
                  onChange={(e) => setDate(e.target.value)}
                  className="w-[180px]"
                />
              </div>
              <div className="space-y-1 flex-1 min-w-[260px]">
                <Label>{t("heat_meters.manual_meter")}</Label>
                <select
                  value={meterId}
                  onChange={(e) => setMeterId(e.target.value)}
                  className="w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm"
                >
                  {rows.map(({ meter, client }) => (
                    <option key={meter.id} value={meter.id}>
                      {client.name} — {meter.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {selected && selected.meter.source_id === null && (
              <div className="text-sm text-amber-700 bg-amber-50 rounded-md p-2">
                meter has no source_id — bind it on the Clients page first
              </div>
            )}

            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50">
                  <tr className="text-left text-slate-500">
                    <th className="p-2 w-20">{t("heat_meters.manual_hour")}</th>
                    <th className="p-2 text-right">{t("heat_meters.heat_gcal")}</th>
                    <th className="p-2 w-32" />
                  </tr>
                </thead>
                <tbody>
                  {Array.from({ length: 24 }, (_, h) => h).map((hour) => (
                    <ManualRow
                      key={hour}
                      hour={hour}
                      date={date}
                      meterId={meterId}
                      initialValue={byHour.get(hour)}
                      canEdit={canEdit && selected?.meter.source_id !== null}
                      onSaved={() => {
                        void qc.invalidateQueries({
                          queryKey: ["telemetry-by-meter", meterId, date],
                        });
                      }}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}

function ManualRow({
  hour,
  date,
  meterId,
  initialValue,
  canEdit,
  onSaved,
}: {
  hour: number;
  date: string;
  meterId: string;
  initialValue: number | undefined;
  canEdit: boolean;
  onSaved: () => void;
}) {
  const { t } = useTranslation();
  const [value, setValue] = useState<string>(
    initialValue !== undefined ? String(initialValue) : "",
  );
  const [error, setError] = useState<string | null>(null);

  // Re-sync if server data changed (different date or meter)
  const initStr = initialValue !== undefined ? String(initialValue) : "";
  const [synced, setSynced] = useState(initStr);
  if (synced !== initStr) {
    setSynced(initStr);
    setValue(initStr);
  }

  const save = useMutation({
    mutationFn: () =>
      telemetryApi.putManual({
        meter_id: meterId,
        ts: isoForHour(date, hour),
        metric: "heat_gcal",
        value: Number(value || 0),
      }),
    onSuccess: () => {
      setError(null);
      onSaved();
    },
    onError: (e: unknown) => {
      const code = (e as AxiosError<{ error?: { code?: string } }>)?.response?.data?.error?.code;
      setError(
        code === "period_closed"
          ? t("plan.errors.period_closed")
          : code === "meter_source_missing"
            ? "meter_source_missing"
            : t("clients.errors.unknown"),
      );
    },
  });

  return (
    <tr className="border-t border-slate-100">
      <td className="p-2 font-mono">{padHour(hour)}:00</td>
      <td className="p-2 text-right">
        <Input
          type="number"
          step="0.001"
          min={0}
          value={value}
          onChange={(e) => setValue(e.target.value)}
          disabled={!canEdit}
          className="w-32 text-right ml-auto"
        />
      </td>
      <td className="p-2">
        {canEdit && (
          <Button
            size="sm"
            variant="outline"
            onClick={() => save.mutate()}
            disabled={save.isPending || value === synced}
          >
            <Save className="h-4 w-4" /> {save.isPending ? t("common.saving") : t("heat_meters.save_row")}
          </Button>
        )}
        {error && <div className="text-xs text-red-600 mt-1">{error}</div>}
      </td>
    </tr>
  );
}
